import { AiLoadingSpinner } from "@/components/ui/ai-loading-spinner"
import { useEffect, useRef, useState } from "react"
import TextTransition, { presets } from "react-text-transition"

export const Loading = ({
  text,
  size,
  progressValue = 0,
  autoIncrementIntervalMs,
  onEndReached,
}: {
  text?: string
  size?: number
  progressValue?: number
  autoIncrementIntervalMs?: number
  onEndReached?: () => void
}) => {
  const [spinnerValue, setSpinnerValue] = useState(progressValue)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasCompletedRef = useRef(false)

  useEffect(() => {
    setSpinnerValue(progressValue)
    if (progressValue < 100) {
      hasCompletedRef.current = false
    }
  }, [progressValue])

  useEffect(() => {
    if (autoIncrementIntervalMs) {
      const intervalId = setInterval(() => {
        setSpinnerValue((c) => {
          const nextValue = Math.min(c + 1, 100)
          if (nextValue === 100 && intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          return nextValue
        })
      }, autoIncrementIntervalMs)
      intervalRef.current = intervalId
      return () => {
        clearInterval(intervalId)
        intervalRef.current = null
      }
    }
    return undefined
  }, [autoIncrementIntervalMs])

  useEffect(() => {
    if (spinnerValue === 100 && !hasCompletedRef.current) {
      hasCompletedRef.current = true
      onEndReached?.()
    }
  }, [spinnerValue, onEndReached])

  return (
    <div className="flex flex-col items-center justify-center">
      <AiLoadingSpinner
        size={size}
        text={spinnerValue === 0 ? "" : `${spinnerValue}%`}
      />
      {text ? (
        <div className="mt-5 min-h-6 w-full max-w-144 animate-pulse px-4 text-center">
          <div className="w-full text-center">
            <TextTransition springConfig={presets.wobbly}>
              {text}
            </TextTransition>
          </div>
        </div>
      ) : null}
    </div>
  )
}
